import {
	ConfirmationDialog,
	FlatIconButton,
	Icon,
	Id,
	RadioControl,
	SingleSelectField,
	TextButton,
	TextField,
	Trigger,
} from "@dgs/core";
import { FieldArray, useField } from "formik";
import { TFunction } from "i18next";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ContactAnchor, ContactMeans } from "../../ui/ContactMeans";
import {
	ContactAttributeRequest,
	IContactAttributeBaseHandler,
	IContactAttributeHandler,
	IContactAttributeListHandler,
} from "../IContactAttributeHandler";
import { RelationListRow, RelationListSection, TwoColumn } from "./RelationListSection";
import { usePrimaryRelationFieldArray } from "./usePrimaryRelationFieldArray";

const typeOptions = ["privateMobile", "workMobile", "privateLandline", "workLandline", "central", "other"];

interface PhoneValue {
	id: Id | null;
	phoneNumber: string;
	type: "privateMobile" | "workMobile" | "privateLandline" | "workLandline" | "central" | "other" | null;
	isPrimary: boolean;
}

const emptyValue: PhoneValue = {
	id: null,
	phoneNumber: "",
	type: null,
	isPrimary: false,
};

const isEmpty = (value: PhoneValue) => value.phoneNumber === "" && value.type === null;

export function createPhonesContactAttributeBaseHandler(id: Id): IContactAttributeBaseHandler {
	return {
		type: "PHONES",
		id,
		getLabel: (t) => t("Phones"),
	};
}

export function createPhonesContactAttributeProfileHandler(
	id: Id,
	value: PhoneValue[],
): IContactAttributeHandler<PhoneValue[]> {
	const baseHandler = createPhonesContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		value: value.length === 0 ? [emptyValue] : value,
		validate(t, values) {
			const errors: Record<number, any> = {};
			let hasPrimary = false;
			let hasNonEmptyValues = false;
			values.forEach((phone, index) => {
				if (isEmpty(phone)) return;
				hasPrimary ||= phone.isPrimary;
				hasNonEmptyValues = true;
				if (!phone.phoneNumber) {
					errors[index] = { ...(errors[index] || {}), phoneNumber: t("Field is required") };
				}
				if (!phone.type) {
					errors[index] = { ...(errors[index] || {}), type: t("Field is required") };
				}
			});

			if (!hasPrimary && hasNonEmptyValues) {
				return t("At least one phone number must be marked as primary");
			}

			return errors;
		},
		renderValue(): ReactNode {
			if (value.length === 0) return "-";
			return (
				<ContactMeans
					items={value.map(({ phoneNumber }) => phoneNumber)}
					render={(phoneNumber) => <ContactAnchor type="tel">{phoneNumber}</ContactAnchor>}
				/>
			);
		},
		renderFormField(_: TFunction, prefix): ReactNode {
			const name = `${prefix ? `${prefix}.` : ""}${id}.value`;
			return <FormField name={name} />;
		},
		toRequestValue(): ContactAttributeRequest<PhoneValue[]> {
			return { id, value: this.value.filter((phone) => !isEmpty(phone)) };
		},
	};
}

export function createPhonesContactAttributeListHandler(id: Id): IContactAttributeListHandler {
	const baseHandler = createPhonesContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		getColumnSize: () => "1fr",
	};
}

const FormField: FC<{ name: string }> = ({ name }) => {
	const { t } = useTranslation();
	const { items, replace, push, remove } = usePrimaryRelationFieldArray<PhoneValue>(name, emptyValue);
	const [, { error }] = useField(name);
	const hasOnlyOneEmptyItem = items.length === 1 && isEmpty(items[0]);

	return (
		<FieldArray name={name}>
			{() => (
				<RelationListSection title={t("Phone numbers")} error={typeof error === "string" ? error : undefined}>
					{items.map((phone, index) => (
						<RelationListRow key={index}>
							<TwoColumn>
								<TextField type="tel" name={`${name}.${index}.phoneNumber`} label={t("Phone number")} required />
								<SingleSelectField
									name={`${name}.${index}.type`}
									options={typeOptions}
									getLabel={(option) => t(option)}
									getValue={String}
									label={t("Type")}
									clearable={false}
									required
								/>
								<RadioControl
									id={`phones-${index}-primary`}
									name="phones.isPrimary"
									value={String(index)}
									checked={phone.isPrimary}
									label={t("Mark as primary")}
									onChange={(e) => replace(index, { ...phone, isPrimary: e.target.value === String(index) })}
								/>
							</TwoColumn>
							<Trigger
								render={(dialogProps) => (
									<ConfirmationDialog
										heading={t("Delete phone number")}
										labels={{ confirm: t("Delete"), close: t("Cancel") }}
										open={dialogProps.open}
										onClose={dialogProps.handleClose}
										action={(close) => {
											remove(index);
											close();
										}}
									>
										{t("Are you sure you want to delete this phone number?")}
									</ConfirmationDialog>
								)}
							>
								{(triggerProps) => (
									<FlatIconButton
										{...triggerProps}
										type="button"
										title={t("Delete")}
										icon="delete"
										disabled={hasOnlyOneEmptyItem}
									/>
								)}
							</Trigger>
						</RelationListRow>
					))}
					<span>
						<TextButton onClick={() => push(emptyValue)} type="button" prefix={<Icon $icon="plus" />}>
							{t("Add phone number")}
						</TextButton>
					</span>
				</RelationListSection>
			)}
		</FieldArray>
	);
};
